import { useEffect, useMemo } from 'react'
import { useParams, generatePath, useNavigate } from 'react-router-dom'

import Page from 'commons/page'
import Block from 'components/block'
import ButtonV2 from 'components/button-v2'
import { SectionTitle, Text } from 'components/typography'
import { useAuthProvider } from 'contexts/auth'
import { useAppDispatch, useAppSelector } from 'hooks'
import AccessDenied from 'pages/access-denied'
import {
  fetchContractById,
  selectContractById,
  selectContractByIdFailed,
  selectContractByIdLoading,
  selectFeatureByIdFailed,
} from 'stores/contract'

import styled from 'styled-components'
import { UserRole } from '../../schema/user'
import { ActiveInventoryItem } from '../../schema/contract'
import SubPageHeader from '../../components/sub-page-header'
import ActiveInventoryList from '../../features/contracts/list/active-inventory-list'
import { Horizontal } from '../../components/layout'
import ROUTING_PATH from '../../constants/route'

const SectionHeader = styled(Horizontal)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`

const ActiveInventoriesListWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  justify-items: center;

  @media only screen and (min-width: ${(p) => p.theme.vp}px) {
    grid-template-columns: repeat(3, 1fr);
    justify-items: stretch;
  }
`
const ActiveInventories = () => {
  const { claims } = useAuthProvider()
  const isOwner = useMemo(() => claims?.role === UserRole.OWNER, [claims?.role])
  const contract = useAppSelector(selectContractById)
  const contractLoading = useAppSelector(selectContractByIdLoading)
  const contractFailed = useAppSelector(selectContractByIdFailed)
  const featureFailed = useAppSelector(selectFeatureByIdFailed)
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    if (!claims?.role) {
      return
    }

    if (!id) {
      return
    }

    dispatch(fetchContractById({ role: claims?.role, id }))
  }, [dispatch, claims?.role, id])
  const reFetch = () => {
    dispatch(fetchContractById({ role: claims!.role!, id: id! }))
  }
  const data = useMemo(() => {
    return contract
  }, [contract])

  const HasApiError = useMemo(() => {
    return contractFailed || featureFailed
  }, [contractFailed, featureFailed])

  const assignNumbers = (items: ActiveInventoryItem[]) => {
    let currentItemNumber = 1
    return items.map((item) => {
      const isVisible = item.isVisible !== undefined ? item.isVisible : true
      const isLocated = item.lat && item.lon
      const itemNumberLabel =
        isVisible && isLocated ? String(currentItemNumber++) : '-'
      return {
        ...item,
        itemNumberLabel,
      }
    })
  }

  const { activeInventories } = useMemo(() => {
    if (!data) {
      return {
        activeInventories: [],
      }
    }
    const numberedActiveInventories = assignNumbers(
      data.activeInventory?.items ?? []
    )

    return {
      activeInventories: numberedActiveInventories,
    }
  }, [data])

  const handleMapButtonClick = () => {
    const mapPath = generatePath(
      claims?.role === UserRole.OWNER
        ? ROUTING_PATH.OwnerActiveInventoriesMap
        : ROUTING_PATH.AgentActiveInventoriesMap,
      { id: id! }
    )
    navigate(mapPath)
  }

  return (
    <>
      {HasApiError && <AccessDenied />}
      {!HasApiError && (
        <Page
          title={`周辺の売出物件`}
          loading={contractLoading}
          type="user"
          customHeader={<SubPageHeader />}
        >
          {activeInventories.length !== 0 && (
            <Block>
              <SectionHeader>
                <SectionTitle>周辺の売出物件</SectionTitle>
                <ButtonV2
                  variant="secondary"
                  size="sm"
                  onClick={handleMapButtonClick}
                >
                  <img src="/image/map.svg" alt="地図" width="16" height="16" />
                  地図を見る
                </ButtonV2>
              </SectionHeader>
              {!isOwner && (
                <Text size={'sm'}>
                  売主様の画面に表示するかを物件毎に設定できます。
                </Text>
              )}
              <ActiveInventoriesListWrapper>
                <ActiveInventoryList
                  list={activeInventories}
                  contractId={id!}
                  isOwner={isOwner}
                  reFetch={reFetch}
                  isVertical={true}
                  background="white"
                />
              </ActiveInventoriesListWrapper>
            </Block>
          )}
        </Page>
      )}
    </>
  )
}

export default ActiveInventories
